import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { readIslandInnerHtml } from "../../lib/htmlBlockRuntime";
import {
  applyCustomCssInDocument,
  applyCustomJavaScriptInDocument,
  computeHtmlSrcDocFingerprint,
  prepareSrcDocHtml,
} from "../../lib/htmlIframeSrcDoc";
import { morphPreparedHtmlIntoDocument } from "../../lib/morphIframeDocument";
import { cn } from "../../lib/utils";

export type HtmlDocumentMode = "interactive" | "edit";

export type HtmlDocumentProps = {
  html: string;
  mode: HtmlDocumentMode;
  title?: string;
  className?: string;
  /**
   * Optional content hash for fingerprint gating (falls back to body hash).
   *
   * MUST identify the `html` prop actually passed: capture the hash together
   * with the html snapshot it belongs to, never a live value that can update
   * ahead of `html`. The fingerprint prefers `contentHash` over hashing the
   * body, so a hash that arrives before its html commits the new fingerprint
   * against the old document — the eventual html delivery then no-ops and the
   * document is stuck on stale content.
   */
  contentHash?: string | null;
  /**
   * Dirty region ids — skipped during morph so local drafts survive updates.
   *
   * A reactive value rather than a getter: besides protecting drafts at morph
   * time, it is the only signal that tells a deferred `colorScheme` flip when
   * the document has become safe to reload (see `colorScheme`). Pass a set
   * that changes identity whenever the dirty set does.
   */
  protectedRegionIds?: ReadonlySet<string>;
  /** Extra fingerprint salt (e.g. known-missing asset ids). */
  fingerprintExtras?: Iterable<string>;
  /**
   * Trusted CSS injected into the iframe document. Application-controlled
   * only — never pass untrusted user input.
   */
  customCss?: string;
  /**
   * Trusted JavaScript executed inside the iframe. Must be idempotent: it
   * runs after the initial load and after each successful document morph
   * (and again after a fallback reload).
   */
  customJavaScript?: string;
  /**
   * Token/theme CSS injected at the FRONT of `<head>` (same transport as the
   * canvas block card): prepended content loses cascade ties, so the
   * document's own CSS can deliberately override the injected tokens —
   * identical override behavior on both surfaces. Pair with `colorScheme`.
   */
  extraHeadCss?: string;
  /** Prepend the Cohesive font `<link>` elements (canvas-block parity). */
  injectFontLinks?: boolean;
  /**
   * Render theme-matched instead of the default force-light "paper" preview.
   * Omit for legacy documents — their appearance must not change.
   *
   * Set it only for documents authored against the Cohesive token vocabulary
   * (see `usesCohesiveBlockTokens`), and pair it with
   * `extraHeadCss={getCohesiveBlockTokensCss()}` + `injectFontLinks` so the
   * tokens the document references actually resolve.
   *
   * A change forces a reload rather than a morph (the injected color scheme
   * lives in `<head>`), so it is deferred while an island is focused or dirty
   * — otherwise a theme flip mid-sentence would discard the draft.
   */
  colorScheme?: "light" | "dark";
  onRegionFocus?: (regionId: string, content: string) => void;
  onRegionEdit?: (regionId: string, content: string) => void;
  onRegionBlur?: (regionId: string, content: string) => void;
  /** Cmd/Ctrl+S while focused in an island. */
  onSaveRequest?: () => void;
  onAssetError?: (url: string) => void;
  onLoad?: () => void;
  /** Called when an in-place morph fails and the component falls back to srcDoc. */
  onMorphError?: (error: unknown) => void;
  /**
   * Fired synchronously before the live document is replaced by a full reload
   * (mode change or morph fallback). The old document is still intact, so
   * consumers can persist unsaved region edits before they are discarded.
   */
  onBeforeReload?: () => void;
};

export type HtmlDocumentHandle = {
  getDocument: () => Document | null;
};

// Non-typed keys presentation frameworks (reveal.js, impress.js, …) bind for
// slide nav. Only these are suppressed while editing — never letters like N/P/F
// that frameworks also bind but the user needs to type.
const PRESENTATION_NAV_KEYS = new Set([
  " ",
  "Spacebar",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
]);

function getEditableIsland(target: EventTarget | null): HTMLElement | null {
  // Duck-type across the iframe boundary: `instanceof Element` uses the
  // parent realm's constructor and is always false for srcDoc nodes.
  if (!target || !("nodeType" in target)) return null;
  const node = target as Node;
  const el =
    node.nodeType === 1
      ? (node as Element)
      : ((node as ChildNode).parentElement ?? null);
  if (!el || typeof el.closest !== "function") return null;
  return el.closest<HTMLElement>("[data-edit-id]:not([data-no-edit])");
}

/**
 * Sandboxed HTML document viewer with optional island-based inline editing.
 *
 * - `interactive` — normal iframe document (scripts, links, presentation nav)
 * - `edit` — `[data-edit-id]` islands become contenteditable; region changes
 *   surface via callbacks. Persistence is the caller's responsibility.
 */
export const HtmlDocument = forwardRef<HtmlDocumentHandle, HtmlDocumentProps>(
  function HtmlDocument(
    {
      html,
      mode,
      title = "HTML document",
      className,
      contentHash,
      protectedRegionIds,
      fingerprintExtras,
      customCss,
      customJavaScript,
      extraHeadCss,
      injectFontLinks = false,
      colorScheme,
      onRegionFocus,
      onRegionEdit,
      onRegionBlur,
      onSaveRequest,
      onAssetError,
      onLoad,
      onMorphError,
      onBeforeReload,
    },
    ref,
  ) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const iframeCleanupRef = useRef<(() => void) | null>(null);
    const hasEverShownContentRef = useRef(false);
    const srcDocFingerprintRef = useRef<string | null>(null);
    const lastModeRef = useRef<HtmlDocumentMode | null>(null);
    const activeIslandIdRef = useRef<string | null>(null);
    const [srcDoc, setSrcDoc] = useState<string | null>(null);
    const [iframeLoaded, setIframeLoaded] = useState(false);

    // The scheme actually delivered, which lags `colorScheme` whenever a flip
    // arrived mid-edit. `undefined` means "nothing delivered yet".
    const appliedColorSchemeRef = useRef<"light" | "dark" | undefined>(
      undefined,
    );
    const hasDeliveredRef = useRef(false);
    // Bumped on island blur so a deferred flip re-runs the delivery effect;
    // losing focus alone changes no other reactive input.
    const [deferredSchemeNonce, setDeferredSchemeNonce] = useState(0);
    const schemeDeferredRef = useRef(false);

    const callbacksRef = useRef({
      onRegionFocus,
      onRegionEdit,
      onRegionBlur,
      onSaveRequest,
      onAssetError,
      onLoad,
      onMorphError,
      onBeforeReload,
      protectedRegionIds,
      customCss,
      customJavaScript,
      mode,
    });
    useEffect(() => {
      callbacksRef.current = {
        onRegionFocus,
        onRegionEdit,
        onRegionBlur,
        onSaveRequest,
        onAssetError,
        onLoad,
        onMorphError,
        onBeforeReload,
        protectedRegionIds,
        customCss,
        customJavaScript,
        mode,
      };
    });

    const getDocument = useCallback(
      () =>
        iframeRef.current?.contentDocument ??
        iframeRef.current?.contentWindow?.document ??
        null,
      [],
    );

    useImperativeHandle(ref, () => ({ getDocument }), [getDocument]);

    const applyCustomInjections = useCallback((doc: Document) => {
      applyCustomCssInDocument(doc, callbacksRef.current.customCss);
      applyCustomJavaScriptInDocument(
        doc,
        callbacksRef.current.customJavaScript,
      );
    }, []);

    const setupIframeInteractions = useCallback(() => {
      const iframeEl = iframeRef.current;
      const doc =
        iframeEl?.contentDocument ?? iframeEl?.contentWindow?.document;
      if (!iframeEl || !doc?.body) return;

      try {
        iframeCleanupRef.current?.();
      } catch {
        /* iframe may already be detached */
      }
      iframeCleanupRef.current = null;

      const isEditMode = () => callbacksRef.current.mode === "edit";

      const onFocusIn = (e: FocusEvent) => {
        if (!isEditMode()) return;
        const el = getEditableIsland(e.target);
        if (!el) return;
        const editId = el.dataset.editId;
        if (!editId) return;
        activeIslandIdRef.current = editId;
        // readIslandInnerHtml, not innerHTML: the editable prepare pass
        // marker-injects scaffolding (button tabindex/type) that must never
        // reach autosave baselines or payloads.
        callbacksRef.current.onRegionFocus?.(editId, readIslandInnerHtml(el));
      };

      const onFocusOut = (e: FocusEvent) => {
        if (!isEditMode()) return;
        const fromIsland = getEditableIsland(e.target);
        const toIsland = getEditableIsland(e.relatedTarget);
        if (fromIsland) {
          const editId = fromIsland.dataset.editId;
          if (editId) {
            callbacksRef.current.onRegionBlur?.(
              editId,
              readIslandInnerHtml(fromIsland),
            );
          }
        }
        if (fromIsland && toIsland) return;
        activeIslandIdRef.current = null;
        // Focus loss is not otherwise reactive, so a scheme flip parked while
        // this island was focused would sit unapplied until the next update.
        if (schemeDeferredRef.current) {
          setDeferredSchemeNonce((n) => n + 1);
        }
      };

      const onInput = (e: Event) => {
        if (!isEditMode()) return;
        const el = getEditableIsland(e.target);
        if (!el) return;
        const editId = el.dataset.editId;
        if (!editId) return;
        callbacksRef.current.onRegionEdit?.(editId, readIslandInnerHtml(el));
      };

      const onKeyDown = (e: Event) => {
        if (!isEditMode()) return;
        const el = getEditableIsland(e.target);
        if (!el) return;
        e.stopPropagation();

        const ke = e as KeyboardEvent;
        if (ke.key === "Enter" && !ke.shiftKey) {
          e.preventDefault();
          doc.execCommand("insertLineBreak");
        }
        if (ke.key === "s" && (ke.metaKey || ke.ctrlKey)) {
          e.preventDefault();
          callbacksRef.current.onSaveRequest?.();
        }
      };

      const onPaste = (e: Event) => {
        if (!isEditMode()) return;
        const el = getEditableIsland(e.target);
        if (!el) return;
        e.preventDefault();
        const text =
          (e as ClipboardEvent).clipboardData?.getData("text/plain") ?? "";
        doc.execCommand("insertText", false, text);
      };

      // While editing an island, suppress presentation-framework slide-nav keys
      // so they drive the caret / insert text instead.
      const navKeyTarget: Window | Document = iframeEl.contentWindow ?? doc;
      const onNavKeyCapture = (e: Event) => {
        if (!isEditMode()) return;
        const ke = e as KeyboardEvent;
        if (!PRESENTATION_NAV_KEYS.has(ke.key)) return;
        const active = doc.activeElement as HTMLElement | null;
        if (!active?.isContentEditable || !getEditableIsland(active)) return;
        ke.stopPropagation();
        ke.stopImmediatePropagation();
      };

      const onAssetError = (e: Event) => {
        const target = e.target;
        if (!target || target === doc.defaultView) return;
        const el = target as HTMLElement & { src?: string; href?: string };
        const url = el.src || el.href;
        if (!url || typeof url !== "string") return;
        callbacksRef.current.onAssetError?.(url);
      };

      doc.addEventListener("focusin", onFocusIn);
      doc.addEventListener("focusout", onFocusOut);
      doc.addEventListener("input", onInput);
      doc.addEventListener("keydown", onKeyDown);
      doc.addEventListener("paste", onPaste);
      navKeyTarget.addEventListener("keydown", onNavKeyCapture, true);
      navKeyTarget.addEventListener("keyup", onNavKeyCapture, true);
      doc.addEventListener("error", onAssetError, true);

      iframeCleanupRef.current = () => {
        try {
          doc.removeEventListener("focusin", onFocusIn);
          doc.removeEventListener("focusout", onFocusOut);
          doc.removeEventListener("input", onInput);
          doc.removeEventListener("keydown", onKeyDown);
          doc.removeEventListener("paste", onPaste);
          navKeyTarget.removeEventListener("keydown", onNavKeyCapture, true);
          navKeyTarget.removeEventListener("keyup", onNavKeyCapture, true);
          doc.removeEventListener("error", onAssetError, true);
        } catch {
          /* iframe may already be detached */
        }
      };
    }, []);

    const ensureIframeInteractions = useCallback(() => {
      setTimeout(() => {
        setupIframeInteractions();
      }, 0);
    }, [setupIframeInteractions]);

    const ensureLayoutReflow = useCallback(() => {
      setTimeout(() => {
        iframeRef.current?.contentDocument?.documentElement.classList.add(
          "__cohesive_kick",
        );
        iframeRef.current?.contentDocument?.documentElement.classList.remove(
          "__cohesive_kick",
        );
      }, 0);
    }, []);

    const handleIframeLoad = useCallback(() => {
      if (!hasEverShownContentRef.current) {
        hasEverShownContentRef.current = true;
        setIframeLoaded(true);
      }
      activeIslandIdRef.current = null;
      const doc = getDocument();
      if (doc) {
        // srcDoc already ran the custom script at end of body; only CSS needs
        // re-syncing in case the props changed while the document loaded.
        applyCustomCssInDocument(doc, callbacksRef.current.customCss);
      }
      ensureIframeInteractions();
      callbacksRef.current.onLoad?.();
    }, [getDocument, ensureIframeInteractions]);

    // Deliver / morph content when identity changes.
    useEffect(() => {
      const editable = mode === "edit";

      // A scheme flip rewrites <head> and therefore costs a reload. Hold it
      // back while the user is mid-edit; the blur handler and the dirty-set
      // change both re-run this effect once it is safe.
      const isEditing =
        activeIslandIdRef.current !== null ||
        (callbacksRef.current.protectedRegionIds?.size ?? 0) > 0;
      const deferScheme =
        hasDeliveredRef.current &&
        isEditing &&
        colorScheme !== appliedColorSchemeRef.current;
      schemeDeferredRef.current = deferScheme;
      const effectiveColorScheme = deferScheme
        ? appliedColorSchemeRef.current
        : colorScheme;

      const prepared = prepareSrcDocHtml(html, {
        editable,
        customCss,
        customJavaScript,
        extraHeadCss,
        injectFontLinks,
        colorScheme: effectiveColorScheme,
      });
      const baseFingerprint = computeHtmlSrcDocFingerprint(
        html,
        contentHash ?? undefined,
        fingerprintExtras,
      );
      const fingerprint = `${baseFingerprint}:${mode}:${customCss ?? ""}:${customJavaScript ?? ""}:${extraHeadCss ?? ""}:${injectFontLinks ? "f" : ""}:${effectiveColorScheme ?? ""}`;
      if (fingerprint === srcDocFingerprintRef.current) return;

      const modeChanged =
        lastModeRef.current !== null && lastModeRef.current !== mode;
      const schemeChanged =
        hasDeliveredRef.current &&
        effectiveColorScheme !== appliedColorSchemeRef.current;
      lastModeRef.current = mode;
      appliedColorSchemeRef.current = effectiveColorScheme;
      hasDeliveredRef.current = true;
      srcDocFingerprintRef.current = fingerprint;

      const doc = getDocument();
      if (
        hasEverShownContentRef.current &&
        doc?.documentElement &&
        !modeChanged &&
        !schemeChanged
      ) {
        try {
          morphPreparedHtmlIntoDocument(doc, prepared, {
            protectedEditIds: callbacksRef.current.protectedRegionIds,
            userIsEditing: activeIslandIdRef.current !== null,
          });
          // Morph updates nodes but does not re-execute scripts.
          applyCustomInjections(doc);
          ensureIframeInteractions();
          ensureLayoutReflow();
          return;
        } catch (err) {
          callbacksRef.current.onMorphError?.(err);
          // Fall through to full srcDoc reload.
        }
      }

      // A full reload throws away the live DOM, including unsaved island
      // drafts. Notify while the old document is still readable.
      if (hasEverShownContentRef.current) {
        callbacksRef.current.onBeforeReload?.();
      }

      setSrcDoc(prepared);
      ensureIframeInteractions();
      ensureLayoutReflow();
    }, [
      html,
      mode,
      contentHash,
      fingerprintExtras,
      customCss,
      customJavaScript,
      extraHeadCss,
      injectFontLinks,
      colorScheme,
      protectedRegionIds,
      deferredSchemeNonce,
      getDocument,
      applyCustomInjections,
      ensureIframeInteractions,
      ensureLayoutReflow,
    ]);

    useEffect(() => {
      return () => {
        try {
          iframeCleanupRef.current?.();
        } catch {
          /* ignore */
        }
        iframeCleanupRef.current = null;
      };
    }, []);

    // Track the *delivered* scheme, not the prop: during a deferred flip the
    // embedder must keep matching what is actually inside the frame, or the
    // browser composites an opaque backstop behind it.
    const activeScheme = appliedColorSchemeRef.current ?? colorScheme;
    const themeMatched = activeScheme !== undefined;
    const embedderScheme = activeScheme ?? "light";

    return (
      <div
        className={cn("relative h-full w-full", className)}
        style={{ colorScheme: embedderScheme }}
      >
        {srcDoc && (
          <iframe
            ref={iframeRef}
            srcDoc={srcDoc}
            title={title}
            className={cn(
              "h-full w-full border-0 transition-opacity duration-300",
              // Legacy documents keep the "paper" preview backdrop; theme-matched
              // ones sit on the app's own surface token instead.
              !themeMatched && "bg-paper",
              iframeLoaded ? "opacity-100" : "opacity-0",
            )}
            style={{
              colorScheme: embedderScheme,
              ...(themeMatched ? { background: "var(--n-00)" } : {}),
            }}
            sandbox="allow-same-origin allow-scripts allow-popups"
            onLoad={handleIframeLoad}
          />
        )}
      </div>
    );
  },
);

HtmlDocument.displayName = "HtmlDocument";
